// components/TemplateHub.jsx
// Template management hub for the sidebar - allows creating templates and generating reports from templates
import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    Button,
    Modal,
    Alert,
    Spinner,
    Badge,
    Form,
    ListGroup
} from 'react-bootstrap';
import { apifetch } from '../services/interceptors';
import {
    FaPaintBrush,
    FaPlus,
    FaEdit,
    FaTrash,
    FaCopy,
    FaFileAlt,
    FaPlay,
    FaEye,
    FaTimes,
    FaCheck,
    FaFolder,
    FaSearch,
    FaClock
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../ThemeContext';
import {
    getAvailableTemplates,
    deleteTemplate,
    cloneTemplate,
    normalizeTemplateId,
    isCustomTemplate
} from '../services/templateService';
import { getUserId } from '../services/authService';
import './TemplateHub.css';

function TemplateHub() {
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();

    // State
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentUserId, setCurrentUserId] = useState(null);

    // Modal states
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [templateToDelete, setTemplateToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [showCloneModal, setShowCloneModal] = useState(false);
    const [templateToClone, setTemplateToClone] = useState(null);
    const [cloneName, setCloneName] = useState('');
    const [cloning, setCloning] = useState(false);
    const [licenseType, setLicenseType] = useState('community'); // default lower tier

    const [showScanSelectModal, setShowScanSelectModal] = useState(false);
    const [selectedTemplateForReport, setSelectedTemplateForReport] = useState(null);
    const [scanResults, setScanResults] = useState([]);
    const [organizations, setOrganizations] = useState([]); // Store organizations
    const [loadingScans, setLoadingScans] = useState(false);
    const [selectedScanFile, setSelectedScanFile] = useState(null);
    
    // Multi-step selection state
    const [scanSelectStep, setScanSelectStep] = useState(1); // 1: Select Org, 2: Select Scan
    const [selectedOrgId, setSelectedOrgId] = useState(null);
    const [modalSearchTerm, setModalSearchTerm] = useState('');

    // Infinite scroll state
    const [displayedScansCount, setDisplayedScansCount] = useState(10);
    const scansPerLoad = 10; // Load 10 more scans each time

    // Load templates
    const loadTemplates = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Fetch subscription tier first
            const token = localStorage.getItem('token');
            try {
                const subscriptionResponse = await apifetch('/s1/api/subscription', {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (subscriptionResponse.ok) {
                    const subscriptionData = await subscriptionResponse.json();
                    setLicenseType(subscriptionData.is_pro ? 'pro' : 'community');
                }
            } catch (err) {
                console.error('Error fetching subscription in TemplateHub:', err);
            }

            // Use getAvailableTemplates - backend handles all filtering
            const fetchedTemplates = await getAvailableTemplates();

            // Remove duplicates based on template name (matching TemplateSelectionPage logic)
            const seenNames = new Set();
            const uniqueTemplates = fetchedTemplates.filter(template => {
                const normalizedName = template.name?.toLowerCase().trim();
                if (!normalizedName || seenNames.has(normalizedName)) {
                    return false;
                }
                seenNames.add(normalizedName);
                return true;
            });
            
            console.log(`[TemplateHub] Loaded ${uniqueTemplates.length} unique templates from ${fetchedTemplates.length} total`);
            setTemplates(uniqueTemplates || []);
        } catch (err) {
            console.error('Error loading templates:', err);
            setError('Failed to load templates');
        } finally {
            setLoading(false);
        }
    }, []);

    // Load scan results for report generation
    const loadScanResults = useCallback(async () => {
        try {
            setLoadingScans(true);
            const token = localStorage.getItem('token');

            // First, fetch all organizations for this tenant
            const orgsResponse = await apifetch('s1/api/organizations', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!orgsResponse.ok) {
                throw new Error('Failed to fetch organizations');
            }

            const orgsData = await orgsResponse.json();
            const organizations = orgsData.organizations || [];

            if (organizations.length === 0) {
                setScanResults([]);
                setOrganizations([]);
                return;
            }
            
            setOrganizations(organizations);

            // Extract organization IDs
            const organizationIds = organizations.map(org => org.id);

            // Fetch all scan results in a single batch request
            const scansResponse = await apifetch('s2/api/scan_results/batch', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ organizationIds })
            });

            if (scansResponse.ok) {
                const scansData = await scansResponse.json();
                const scans = scansData.result || [];
                setScanResults(scans);
            } else {
                throw new Error('Failed to fetch scan results');
            }
        } catch (err) {
            console.error('Error loading scan results:', err);
            setError('Failed to load scan data');
        } finally {
            setLoadingScans(false);
        }
    }, []);


    useEffect(() => {
        // Get current user ID from token
        const userId = getUserId();
        setCurrentUserId(userId);
        console.log('[TemplateHub] Current user ID:', userId);
    }, []);

    useEffect(() => {
        loadTemplates();
    }, [loadTemplates]);

    // Clear messages after timeout
    useEffect(() => {
        if (error || success) {
            const timer = setTimeout(() => {
                setError(null);
                setSuccess(null);
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [error, success]);

    // Handlers
    const handleCreateTemplate = () => {
        navigate('/report-builder', {
            state: {
                mode: 'template',
                scanData: null,
                fromTemplateHub: true
            }
        });
    };

    const handleEditTemplate = (template) => {
        // Normalize template ID to ensure correct format
        const normalizedId = normalizeTemplateId(template);
        
        console.log('[TemplateHub] Editing template:', {
            originalId: template.id,
            normalizedId: normalizedId,
            templateName: template.name
        });
        
        navigate('/report-builder', {
            state: {
                mode: 'template',
                templateId: normalizedId,
                fromTemplateHub: true
            }
        });
    };

    const handlePreviewTemplate = (template) => {
        // Normalize template ID to ensure correct format
        const normalizedId = normalizeTemplateId(template);
        
        navigate('/report-builder', {
            state: {
                mode: 'template',
                templateId: normalizedId,
                previewOnly: true,
                fromTemplateHub: true
            }
        });
    };

    const handleCreateReportFromTemplate = (template) => {
        // Normalize template ID before storing
        const templateWithNormalizedId = {
            ...template,
            id: normalizeTemplateId(template)
        };
        
        setSelectedTemplateForReport(templateWithNormalizedId);
        setSelectedScanFile(null);
        setDisplayedScansCount(10); // Reset to initial count
        setScanSelectStep(1); // Reset to Step 1
        setSelectedOrgId(null); // Reset selection
        setModalSearchTerm(''); // Reset search
        loadScanResults();
        setShowScanSelectModal(true);
    };

    // Infinite scroll handler
    const handleScroll = (e) => {
        const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
        if (bottom && displayedScansCount < scanResults.length) {
            setDisplayedScansCount(prev => Math.min(prev + scansPerLoad, scanResults.length));
        }
    };

    const handleConfirmCreateReport = () => {
        if (!selectedScanFile || !selectedTemplateForReport) {
            setError('Please select a scan file');
            return;
        }

        setShowScanSelectModal(false);

        navigate('/report-builder', {
            state: {
                mode: 'report',
                templateId: selectedTemplateForReport.id,
                fileUrl: selectedScanFile,
                fromTemplateHub: true
            }
        });
    };

    const openDeleteModal = (template) => {
        setTemplateToDelete(template);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!templateToDelete) return;

        try {
            setDeleting(true);
            await deleteTemplate(templateToDelete.id);
            setSuccess(`Template "${templateToDelete.name}" deleted successfully`);
            setShowDeleteModal(false);
            setTemplateToDelete(null);
            loadTemplates();
        } catch (err) {
            console.error('Error deleting template:', err);
            setError('Failed to delete template');
        } finally {
            setDeleting(false);
        }
    };

    const openCloneModal = (template) => {
        setTemplateToClone(template);
        setCloneName(`${template.name} (Copy)`);
        setShowCloneModal(true);
    };

    const handleConfirmClone = async () => {
        if (!templateToClone || !cloneName.trim()) return;

        try {
            setCloning(true);
            await cloneTemplate(templateToClone.id, cloneName.trim());
            setSuccess(`Template duplicated as "${cloneName}"`);
            setShowCloneModal(false);
            setTemplateToClone(null);
            setCloneName('');
            loadTemplates();
        } catch (err) {
            console.error('Error cloning template:', err);
            setError('Failed to duplicate template');
        } finally {
            setCloning(false);
        }
    };

    // Filter templates by search
    const filteredTemplates = templates.filter(template => {
        // Apply search filter
        const matchesSearch = template.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            template.description?.toLowerCase().includes(searchTerm.toLowerCase());

        return matchesSearch;
    });

    const defaultTemplates = filteredTemplates.filter(t => t.id === 1 || t.id === 2 || t.is_default === true);
    const nonDefaultTemplates = filteredTemplates.filter(t => !(t.id === 1 || t.id === 2 || t.is_default === true) || String(t.id).startsWith('custom_'));

    const renderTemplateCards = (templatesList, isDefaultGroup) => (
        <Row>
            {templatesList.map((template) => (
                <Col key={template.id} xs={12} sm={6} lg={4} xl={3} className="mb-4">
                    <Card className={`template-card h-100 ${isDarkMode ? 'dark-card' : ''}`}>
                        <Card.Body className="d-flex flex-column">
                            <div className="template-card-header">
                                <h5 className="template-name">{template.name}</h5>
                                <Badge bg={isDefaultGroup ? "warning" : "success"} className="template-badge">
                                    {isDefaultGroup ? "Default" : "Custom"}
                                </Badge>
                            </div>

                            <p className="template-description flex-grow-1">
                                {(() => {
                                    // Parse template configuration to extract widgets
                                    try {
                                        // Templates use configuration.visualTemplate.pages structure
                                        const config = template.configuration;
                                        if (!config || !config.visualTemplate || !config.visualTemplate.pages) {
                                            return template.description || 'No description provided';
                                        }

                                        const pages = config.visualTemplate.pages || [];
                                        const widgetTypes = new Set();
                                        const findingsTypes = new Set();

                                        // Widget types that are considered "findings"
                                        const findingsWidgetTypes = [
                                            'findings-status', 'key-findings', 'findings-grid', 'open-ports',
                                            'open-ports-summary', 'darkweb-summary', 'accounts-summary',
                                            'accounts-admin-summary', 'firewall-summary', 'localhost-firewall-findings-summary',
                                            'localhost-service-risk-summary', 'service-risk-summary',
                                            'network-firewall-findings-summary', 'network-service-risk-summary',
                                            'admins-finding-summary', 'accounts-finding-summary',
                                            'backups-summary', 'overall-risk-score', 'nested-rectangle-findings'
                                        ];

                                        // Widget type to display name mapping
                                        const widgetDisplayNames = {
                                            'overall-risk-score': 'Overall Risk Score',
                                            'key-findings': 'Key Findings',
                                            'findings-status': 'Findings Status',
                                            'findings-grid': 'Findings Grid',
                                            'open-ports': 'Open Ports',
                                            'open-ports-summary': 'Open Ports Summary',
                                            'darkweb-summary': 'Dark Web Summary',
                                            'accounts-summary': 'Accounts Summary',
                                            'accounts-admin-summary': 'Admin Accounts Summary',
                                            'firewall-summary': 'Firewall Summary',
                                            'localhost-firewall-findings-summary': 'Localhost Firewall Findings',
                                            'localhost-service-risk-summary': 'Localhost Service Risk',
                                            'service-risk-summary': 'Service Risk Summary',
                                            'network-firewall-findings-summary': 'Network Firewall Findings',
                                            'network-service-risk-summary': 'Network Service Risk',
                                            'admins-finding-summary': 'Admins Finding Summary',
                                            'accounts-finding-summary': 'Accounts Finding Summary',
                                            'backups-summary': 'Backup Summary',
                                            'nested-rectangle-findings': 'Nested Rectangle Findings',
                                            'category-description': 'Category Description',
                                            'financial-impact': 'Financial Impact',
                                            'company-at-a-glance': 'Company At A Glance',
                                            'category-score-bar': 'Category Score Bar',
                                            'network-backup-status-table': 'Network Backup Status Table',
                                            'backups-cost-summary': 'Backups Cost Summary'
                                        };

                                        // Format widget names for display
                                        const formatWidgetName = (widgetType) => {
                                            // Use display name if available, otherwise format from widget type
                                            if (widgetDisplayNames[widgetType]) {
                                                return widgetDisplayNames[widgetType];
                                            }
                                            // Fallback: format from widget type
                                            return widgetType
                                                .split('-')
                                                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                                .join(' ');
                                        };

                                        // Extract all block types from all pages
                                        pages.forEach(page => {
                                            page.blocks?.forEach(block => {
                                                if (block.type === 'widget' && block.widgetType) {
                                                    const widgetType = block.widgetType;
                                                    widgetTypes.add(widgetType);

                                                    // Check if it's a findings widget
                                                    if (findingsWidgetTypes.some(fwt => widgetType.includes(fwt) || widgetType === fwt)) {
                                                        findingsTypes.add(widgetType);
                                                    }
                                                }
                                            });
                                        });

                                        if (widgetTypes.size === 0) {
                                            return `${pages.length} page${pages.length !== 1 ? 's' : ''} • No widgets`;
                                        }

                                        const parts = [];
                                        const findingsList = Array.from(findingsTypes).map(formatWidgetName);
                                        const widgetsList = Array.from(widgetTypes).map(formatWidgetName);

                                        // Show findings first if any
                                        if (findingsList.length > 0) {
                                            const displayFindings = findingsList.slice(0, 3);
                                            let findingsText = displayFindings.join(', ');
                                            if (findingsList.length > 3) {
                                                findingsText += `, and ${findingsList.length - 3} more`;
                                            }
                                            parts.push(`Findings: ${findingsText}`);
                                        }

                                        // Show other widgets (non-findings)
                                        const nonFindingWidgets = widgetsList.filter(w => !findingsList.includes(w));
                                        if (nonFindingWidgets.length > 0) {
                                            const displayWidgets = nonFindingWidgets.slice(0, 3);
                                            let widgetsText = displayWidgets.join(', ');
                                            if (nonFindingWidgets.length > 3) {
                                                widgetsText += `, and ${nonFindingWidgets.length - 3} more`;
                                            }
                                            parts.push(`Widgets: ${widgetsText}`);
                                        }

                                        // If we only have findings widgets, show them as widgets
                                        if (parts.length === 0 && findingsList.length > 0) {
                                            const displayFindings = findingsList.slice(0, 4);
                                            let findingsText = displayFindings.join(', ');
                                            if (findingsList.length > 4) {
                                                findingsText += `, and ${findingsList.length - 4} more`;
                                            }
                                            return findingsText;
                                        }

                                        if (parts.length === 0) {
                                            return `${pages.length} page${pages.length !== 1 ? 's' : ''} • ${widgetsList.join(', ')}`;
                                        }

                                        // Join with bullet for better readability
                                        return parts.join(' • ');
                                    } catch (e) {
                                        console.error('Error parsing template configuration:', e, template);
                                        return template.description || 'No description provided';
                                    }
                                })()}
                            </p>

                            <div className="template-meta">
                                <small>
                                    <FaClock className="me-2 opacity-75" />
                                    Created: {new Date(template.created_at).toLocaleDateString()}
                                </small>
                            </div>

                            <div className="template-actions">
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => handleCreateReportFromTemplate(template)}
                                    title="Create Report from Template"
                                    className="w-100 mb-2"
                                >
                                    <FaPlay className="me-1" />
                                    Create Report
                                </Button>
                                {!isDefaultGroup && (
                                    <div className="action-icons d-flex gap-2">
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => openDeleteModal(template)}
                                            title="Delete Template"
                                            className="flex-fill"
                                        >
                                            <FaTrash />
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            ))}
        </Row>
    );

    // Render
    return (
        <Container fluid className={`template-hub ${isDarkMode ? 'dark-mode' : ''}`}>
            {/* Header */}
            <Row className="mb-4">
                <Col>
                    <div className="template-hub-header">
                        <div className="header-content">
                            <h2 className="page-title">
                                <FaPaintBrush className="me-2" />
                                Report Templates
                            </h2>
                            <p className="page-subtitle">
                                Create reusable templates or generate reports from existing templates
                            </p>
                        </div>
                        {licenseType === 'pro' && (
                            <Button
                                variant="primary"
                                size="lg"
                                onClick={handleCreateTemplate}
                                className="create-template-btn"
                            >
                                <FaPlus className="me-2" />
                                Create New Template
                            </Button>
                        )}
                    </div>
                </Col>
            </Row>

            {/* Alerts */}
            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {/* Search */}
            <Row className="mb-4">
                <Col md={6} lg={4}>
                    <div className="search-box">
                        <FaSearch className="search-icon" />
                        <Form.Control
                            type="text"
                            placeholder="Search templates..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={isDarkMode ? 'bg-dark text-light' : ''}
                        />
                    </div>
                </Col>
            </Row>

            {/* Template Grid */}
            {loading ? (
                <div className="loading-container">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3">Loading templates...</p>
                </div>
            ) : filteredTemplates.length === 0 ? (
                <div className="empty-state">
                    <FaFolder size={48} className="empty-icon" />
                    <h4>No Templates Found</h4>
                    <p>
                        {searchTerm
                            ? 'No templates match your search criteria.'
                            : "You haven't created any templates yet. Click 'Create New Template' to get started!"
                        }
                    </p>
                    {!searchTerm && (
                        <Button variant="primary" onClick={handleCreateTemplate}>
                            <FaPlus className="me-2" />
                            Create Your First Template
                        </Button>
                    )}
                </div>
            ) : (
                <>
                    {/* Default Templates Section */}
                    {defaultTemplates.length > 0 && (
                        <>
                            <div className="template-section-header mt-4 mb-3">
                                <h6 className={isDarkMode ? 'text-light opacity-75' : 'text-muted'}>
                                    Default Templates <Badge bg="secondary" className="ms-2">{defaultTemplates.length}</Badge>
                                </h6>
                                <hr className={isDarkMode ? 'border-secondary' : ''} />
                            </div>
                            {renderTemplateCards(defaultTemplates, true)}
                        </>
                    )}

                    {/* Non-Default Templates Section - Only for PRO users */}
                    {licenseType === 'pro' && nonDefaultTemplates.length > 0 && (
                        <>
                            <div className="template-section-header mt-4 mb-3">
                                <h6 className={isDarkMode ? 'text-light opacity-75' : 'text-muted'}>
                                    Your Templates <Badge bg="secondary" className="ms-2">{nonDefaultTemplates.length}</Badge>
                                </h6>
                                <hr className={isDarkMode ? 'border-secondary' : ''} />
                            </div>
                            {renderTemplateCards(nonDefaultTemplates, false)}
                        </>
                    )}
                </>
            )}

            {/* Delete Confirmation Modal */}
            <Modal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                centered
                className={isDarkMode ? 'dark-modal' : ''}
            >
                <Modal.Header closeButton className={isDarkMode ? 'bg-dark text-light' : ''}>
                    <Modal.Title>Delete Template</Modal.Title>
                </Modal.Header>
                <Modal.Body className={isDarkMode ? 'bg-dark text-light' : ''}>
                    Are you sure you want to delete <strong>"{templateToDelete?.name}"</strong>?
                    <br />
                    <small className="text-danger">This action cannot be undone.</small>
                </Modal.Body>
                <Modal.Footer className={isDarkMode ? 'bg-dark' : ''}>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleConfirmDelete} disabled={deleting}>
                        {deleting ? <Spinner animation="border" size="sm" /> : <FaTrash className="me-1" />}
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Clone Modal */}
            <Modal
                show={showCloneModal}
                onHide={() => setShowCloneModal(false)}
                centered
                className={isDarkMode ? 'dark-modal' : ''}
            >
                <Modal.Header closeButton className={isDarkMode ? 'bg-dark text-light' : ''}>
                    <Modal.Title>Duplicate Template</Modal.Title>
                </Modal.Header>
                <Modal.Body className={isDarkMode ? 'bg-dark text-light' : ''}>
                    <Form.Group>
                        <Form.Label>New Template Name</Form.Label>
                        <Form.Control
                            type="text"
                            value={cloneName}
                            onChange={(e) => setCloneName(e.target.value)}
                            placeholder="Enter name for the duplicate"
                            className={isDarkMode ? 'bg-dark text-light' : ''}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className={isDarkMode ? 'bg-dark' : ''}>
                    <Button variant="secondary" onClick={() => setShowCloneModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleConfirmClone}
                        disabled={cloning || !cloneName.trim()}
                    >
                        {cloning ? <Spinner animation="border" size="sm" /> : <FaCopy className="me-1" />}
                        Duplicate
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Scan Selection Modal */}
            <Modal
                show={showScanSelectModal}
                onHide={() => setShowScanSelectModal(false)}
                centered
                size="lg"
                className={isDarkMode ? 'dark-modal' : ''}
            >
                <Modal.Header closeButton className={isDarkMode ? 'bg-dark text-light' : ''}>
                    <Modal.Title>
                        <FaFileAlt className="me-2" />
                        {scanSelectStep === 1 ? 'Select Organization' : 'Select Scan Data'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className={`scan-select-body ${isDarkMode ? 'bg-dark text-light' : ''}`}>
                    {selectedTemplateForReport && (
                        <Alert variant="info" className="mb-3">
                            {scanSelectStep === 1 ? 'Step 1: Choose Organization for ' : 'Step 2: Choose Scan Result for '}
                            <strong>{selectedTemplateForReport.name}</strong>
                        </Alert>
                    )}

                    {loadingScans ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2">Loading data...</p>
                        </div>
                    ) : scanResults.length === 0 ? (
                        <div className="text-center py-4">
                            <FaFolder size={32} className="mb-2 text-muted" />
                            <p>No scan data available. Please upload scan data first.</p>
                        </div>
                    ) : (
                        <>
                            {/* Search Box */}
                            <div className="mb-3">
                                <div className="search-box modal-search-box">
                                    <FaSearch className="search-icon" />
                                    <Form.Control
                                        type="text"
                                        placeholder={scanSelectStep === 1 ? "Search organizations..." : "Search scans..."}
                                        value={modalSearchTerm}
                                        onChange={(e) => setModalSearchTerm(e.target.value)}
                                        className={isDarkMode ? 'bg-dark text-light' : ''}
                                    />
                                </div>
                            </div>

                            {/* STEP 1: Organization Selection */}
                            {scanSelectStep === 1 && (
                                <div className="org-list-container" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    <p className="text-muted mb-2 small">Select an organization to view its available scans:</p>
                                    <ListGroup>
                                        {organizations
                                            .filter(org => org.name.toLowerCase().includes(modalSearchTerm.toLowerCase()))
                                            .map(org => {
                                            // Count available scans for this org
                                            const scanCount = scanResults.filter(s => s.organization_id === org.id).length;
                                            
                                            // Alternative: Disable if 0 scans
                                            const isDisabled = scanCount === 0;

                                            return (
                                                <ListGroup.Item
                                                    key={org.id}
                                                    action
                                                    disabled={isDisabled}
                                                    onClick={() => !isDisabled && (() => {
                                                        setSelectedOrgId(org.id);
                                                        setScanSelectStep(2);
                                                        setModalSearchTerm('');
                                                    })()}
                                                    className={`d-flex justify-content-between align-items-center ${isDarkMode ? 'bg-dark text-light' : ''}`}
                                                >
                                                    <div>
                                                        <strong>{org.name}</strong>
                                                        {org.website && <div className="small text-muted">{org.website}</div>}
                                                    </div>
                                                    <Badge bg={isDisabled ? 'secondary' : 'primary'} pill>
                                                        {scanCount} Scan{scanCount !== 1 ? 's' : ''}
                                                    </Badge>
                                                </ListGroup.Item>
                                            );
                                        })}
                                        {/* Show message if no orgs match search */}
                                        {organizations.filter(org => org.name.toLowerCase().includes(modalSearchTerm.toLowerCase())).length === 0 && (
                                            <div className="text-center p-3 text-muted">
                                                {organizations.length === 0 ? 'No organizations found.' : 'No matching organizations found.'}
                                            </div>
                                        )}
                                    </ListGroup>
                                </div>
                            )}

                            {/* STEP 2: Scan Selection */}
                            {scanSelectStep === 2 && (
                                <>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <small style={{ color: isDarkMode ? '#adb5bd' : '#6c757d' }}>
                                            Showing scans for <strong>{organizations.find(o => o.id === selectedOrgId)?.name || 'Unknown Org'}</strong>
                                        </small>
                                    </div>

                                    <div
                                        className="scan-list-container"
                                        style={{ maxHeight: '400px', overflowY: 'auto' }}
                                        onScroll={handleScroll}
                                    >
                                        <ListGroup className="scan-list">
                                            {scanResults
                                                .filter(s => s.organization_id === selectedOrgId)
                                                .filter(s => {
                                                    const term = modalSearchTerm.toLowerCase();
                                                    const filename = (s.file_path?.split('/').pop() || '').toLowerCase();
                                                    return filename.includes(term);
                                                })
                                                .slice(0, displayedScansCount)
                                                .map((scan) => (
                                                    <ListGroup.Item
                                                        key={scan.scan_id}
                                                        action
                                                        active={selectedScanFile === scan.file_path}
                                                        onClick={() => setSelectedScanFile(scan.file_path)}
                                                        className={isDarkMode ? 'bg-dark text-light' : ''}
                                                    >
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <strong>{scan.file_path?.split('/').pop() || 'Unknown'}</strong>
                                                                <br />
                                                                <small style={{ color: isDarkMode ? '#adb5bd' : '#6c757d' }}>
                                                                    Score: {scan.score || 'N/A'}%
                                                                </small>
                                                            </div>
                                                            <div>
                                                                <small className="text-muted">
                                                                    {new Date(scan.created_at).toLocaleDateString()}
                                                                </small>
                                                                {selectedScanFile === scan.file_path && (
                                                                    <FaCheck className="ms-2 text-success" />
                                                                )}
                                                            </div>
                                                        </div>
                                                    </ListGroup.Item>
                                                ))}
                
                                                {/* Show different empty states */}
                                                {scanResults.filter(s => s.organization_id === selectedOrgId).length === 0 ? (
                                                     <div className="text-center p-4">No scans found for this organization.</div>
                                                ) : scanResults
                                                    .filter(s => s.organization_id === selectedOrgId)
                                                    .filter(s => (s.file_path?.split('/').pop() || '').toLowerCase().includes(modalSearchTerm.toLowerCase()))
                                                    .length === 0 ? (
                                                    <div className="text-center p-4">No matching scans found.</div>
                                                ) : null}
                                        </ListGroup>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className={isDarkMode ? 'bg-dark' : ''}>
                    {scanSelectStep === 2 && (
                        <Button 
                            variant="secondary" 
                            className="me-auto"
                            onClick={() => {
                                setScanSelectStep(1);
                                setSelectedScanFile(null); // Optional: clear selection when going back
                                setModalSearchTerm('');
                            }}
                        >
                            ← Back to Organizations
                        </Button>
                    )}
                    
                    <Button variant="secondary" onClick={() => setShowScanSelectModal(false)}>
                        Cancel
                    </Button>
                    
                    {scanSelectStep === 2 && (
                        <Button
                            variant="primary"
                            onClick={handleConfirmCreateReport}
                            disabled={!selectedScanFile}
                        >
                            <FaPlay className="me-1" />
                            Create Report
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </Container>
    );
}

export default TemplateHub;
